import React, { useEffect, useState } from 'react';
import { FILTER_GROUPS } from '../config/filterConfig';
import styles from './ActiveFilters.module.scss';

/**
 * Active Filters Display Component
 * Shows all active filters as removable pills below the product count
 *
 * @param {Object} props
 * @param {string} props.search - Active search query
 * @param {Array} props.categories - Array of selected category slugs
 * @param {Object} props.propertyFilters - Object with filter handles as keys and arrays of values
 * @param {Function} props.onRemoveSearch - Callback to remove search filter
 * @param {Function} props.onRemoveCategory - Callback to remove a category (receives category slug)
 * @param {Function} props.onRemovePropertyFilter - Callback to remove a property filter (receives handle and value)
 * @param {Function} props.onClearAll - Callback to clear all filters
 */
export default function ActiveFilters({
    search = '',
    categories = [],
    propertyFilters = {},
    onRemoveSearch,
    onRemoveCategory,
    onRemovePropertyFilter,
    onClearAll,
}) {
    const [categoryData, setCategoryData] = useState([]);

    // Fetch category data to get titles
    useEffect(() => {
        async function fetchCategories() {
            try {
                const response = await fetch('/intranet/produkte/categories');
                if (!response.ok) {
                    throw new Error('Failed to fetch categories');
                }
                const data = await response.json();
                setCategoryData(data);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        }

        if (categories.length > 0) {
            fetchCategories();
        }
    }, [categories.length]);

    // Helper: Get category title from slug
    const getCategoryTitle = (slug) => {
        for (const parent of categoryData) {
            const child = parent.children?.find(c => c.slug === slug);
            if (child) {
                return child.title;
            }
        }
        return slug; // Fallback to slug if not found
    };

    // Helper: Get filter group display name
    const getFilterGroupDisplay = (handle) => {
        const group = FILTER_GROUPS.find(g => g.handle === handle);
        return group ? group.display : handle;
    };

    // Helper: Get filter option label
    const getFilterOptionLabel = (handle, value) => {
        const group = FILTER_GROUPS.find(g => g.handle === handle);
        if (!group) return value;

        const option = group.options.find(o => o.value === value);
        return option ? option.label : value;
    };

    // Count total active filters
    const hasSearch = search.trim() !== '';
    const categoryCount = categories.length;
    const propertyFilterCount = Object.values(propertyFilters).reduce(
        (total, arr) => total + (arr?.length || 0),
        0
    );
    const totalFilters = (hasSearch ? 1 : 0) + categoryCount + propertyFilterCount;

    // Don't render if no filters are active
    if (totalFilters === 0) {
        return null;
    }

    return (
        <div className={styles.activeFilters}>
            <div className={styles.pills}>
                {/* Clear all pill - only show if more than 1 filters are active */}
                {totalFilters > 1 && (
                    <button
                        type="button"
                        className={`${styles.pill} ${styles.clearAllPill}`}
                        onClick={onClearAll}
                        aria-label="Alle Filter löschen"
                    >
                        <span className={styles.pillLabel}>Alle Filter löschen</span>
                    </button>
                )}

                {/* Search pill */}
                {hasSearch && (
                    <button
                        type="button"
                        className={styles.pill}
                        onClick={onRemoveSearch}
                        aria-label="Suche entfernen"
                    >
                        <span className={styles.pillLabel}>
                            Suche: <strong>{search}</strong>
                        </span>
                        <span className={styles.pillRemove}>×</span>
                    </button>
                )}

                {/* Category pills */}
                {categories.map((categorySlug) => (
                    <button
                        key={categorySlug}
                        type="button"
                        className={styles.pill}
                        onClick={() => onRemoveCategory(categorySlug)}
                        aria-label={`Kategorie ${getCategoryTitle(categorySlug)} entfernen`}
                    >
                        <span className={styles.pillLabel}>
                            {getCategoryTitle(categorySlug)}
                        </span>
                        <span className={styles.pillRemove}>×</span>
                    </button>
                ))}

                {/* Property filter pills */}
                {Object.entries(propertyFilters).map(([handle, values]) => {
                    if (!values || values.length === 0) return null;

                    return values.map((value) => (
                        <button
                            key={`${handle}-${value}`}
                            type="button"
                            className={styles.pill}
                            onClick={() => onRemovePropertyFilter(handle, value)}
                            aria-label={`Filter ${getFilterOptionLabel(handle, value)} entfernen`}
                        >
                            <span className={styles.pillLabel}>
                                <span className={styles.filterGroup}>
                                    {getFilterGroupDisplay(handle)}:
                                </span>{' '}
                                {getFilterOptionLabel(handle, value)}
                            </span>
                            <span className={styles.pillRemove}>×</span>
                        </button>
                    ));
                })}
            </div>
        </div>
    );
}
